import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Accordion, Spinner, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { query, collection, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase.js';
import { useGeocode } from '../hooks/useGeocode.js';
import TrackingMap from '../components/TrackingMap.js';
import { formatDuration } from '../utils/formatters.js';
import './TrackingPage.css';

export default function TrackingPersonPage() {
  const { displayName } = useParams();
  const navigate = useNavigate();
  const { getAddressFromCoords } = useGeocode();

  const [trackings, setTrackings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [trackingsByDate, setTrackingsByDate] = useState({});

  // Carrega rastreamentos da pessoa
  useEffect(() => {
    loadPersonTrackings();
  }, [displayName]);

  const loadPersonTrackings = async () => {
    try {
      setLoading(true);
      setError(null);

      const decodedName = decodeURIComponent(displayName);
      const q = query(
        collection(db, 'timeTrackings'),
        where('displayName', '==', decodedName),
        where('status', '==', 'completed')
      );

      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Ordena por data mais recente
      data.sort((a, b) => b.clockInTime.toDate() - a.clockInTime.toDate());

      setTrackings(data);
      groupByDate(data);
    } catch (err) {
      console.error('Erro ao carregar rastreamentos:', err);
      setError('Erro ao carregar dados de rastreamento');
    } finally {
      setLoading(false);
    }
  };

  const groupByDate = (data) => {
    const grouped = {};

    data.forEach((tracking) => {
      const date = tracking.clockInTime
        .toDate()
        .toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });

      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(tracking);
    });

    setTrackingsByDate(grouped);
  };

  const handleDelete = async (trackingId) => {
    if (!window.confirm('Tem certeza que deseja deletar este rastreamento?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'timeTrackings', trackingId));
      setSuccess('✅ Rastreamento deletado com sucesso!');
      setTrackings((prev) => prev.filter((t) => t.id !== trackingId));
      groupByDate(trackings.filter((t) => t.id !== trackingId));

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Erro ao deletar:', err);
      setError('❌ Erro ao deletar rastreamento');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '--:--';
    return timestamp.toDate().toLocaleTimeString('pt-BR');
  };

  const showLocationDetails = async (tracking) => {
    const clusters = tracking.locationClusters || [];
    const travels = tracking.travelSegments || [];

    let details = `📍 DETALHES DE LOCALIZAÇÃO\n\n`;

    if (clusters.length === 0) {
      details += 'Sem clusters detectados\n';
    } else {
      // Geocodifica endereços de forma assíncrona
      for (let idx = 0; idx < clusters.length; idx++) {
        const cluster = clusters[idx];
        let address = '';

        try {
          address = await getAddressFromCoords(cluster.centerLat, cluster.centerLng);
        } catch (err) {
          console.error('Erro ao geocodificar:', err);
          address = `${cluster.centerLat.toFixed(5)}, ${cluster.centerLng.toFixed(5)}`;
        }

        details += `📌 LOCAL ${idx + 1}\n`;
        details += `   📍 ${address}\n`;
        details += `   Raio: ${Math.round(cluster.radius)}m\n`;
        details += `   ⏱️ Tempo: ${cluster.durationMinutes}min\n`;
        details += `   📍 Pontos GPS: ${cluster.pointsCount}\n\n`;
      }
    }

    if (travels.length > 0) {
      details += `✈️ TRAJETOS\n\n`;
      travels.forEach((travel, idx) => {
        details += `Trajeto ${idx + 1}: Local ${travel.fromCluster + 1} → Local ${travel.toCluster + 1}\n`;
        details += `   ⏱️ Tempo: ${travel.durationMinutes}min\n\n`;
      });
    }

    details += `\n⏱️ RESUMO\n`;
    details += `Tempo parado: ${tracking.totalStationaryMinutes || 0}min\n`;
    details += `Tempo em trajeto: ${tracking.totalTravelMinutes || 0}min\n`;
    details += `Total: ${tracking.totalMinutes}min`;

    alert(details);
  };

  if (loading) {
    return (
      <Container fluid className="py-5">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Carregando rastreamentos...</p>
        </div>
      </Container>
    );
  }

  const decodedName = decodeURIComponent(displayName);

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4 align-items-center">
        <Col>
          <Button variant="outline-primary" onClick={() => navigate('/admin/tracking')} className="mb-3">
            ← Voltar
          </Button>
          <h1>📍 Rastreamento de {decodedName}</h1>
          <small className="text-muted">
            {trackings.length} rastreamento(s) encontrado(s)
          </small>
        </Col>
      </Row>

      {/* Mensagens */}
      {success && <Alert variant="success" className="mb-3">{success}</Alert>}
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {trackings.length === 0 ? (
        <Alert variant="info">
          Nenhum rastreamento encontrado para {decodedName}.
        </Alert>
      ) : (
        <Accordion defaultActiveKey={Object.keys(trackingsByDate)[0]} className="mb-4">
          {Object.entries(trackingsByDate).map(([date, dayTrackings]) => (
            <Accordion.Item eventKey={date} key={date}>
              <Accordion.Header>
                <strong>📅 {date}</strong>
                <Badge bg="info" className="ms-3">
                  {dayTrackings.length} rastreamento(s)
                </Badge>
              </Accordion.Header>
              <Accordion.Body>
                {/* Mapa do dia */}
                <Card className="mb-4">
                  <Card.Header className="bg-transparent border-0 pb-0">
                    <h5 className="mb-0">🗺️ Mapa do Dia</h5>
                  </Card.Header>
                  <Card.Body style={{ height: '400px' }}>
                    <TrackingMap trackings={dayTrackings} />
                  </Card.Body>
                </Card>

                {/* Tabela de rastreamentos do dia */}
                <Card>
                  <Card.Header className="bg-transparent border-0 pb-0">
                    <h5 className="mb-0">📊 Detalhes do Dia</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="table-responsive">
                      <Table hover>
                        <thead>
                          <tr>
                            <th>⏰ Entrada</th>
                            <th>⏰ Saída</th>
                            <th>⏱️ Duração</th>
                            <th>🏠 Tempo Parado</th>
                            <th>✈️ Trajeto</th>
                            <th>📍 Locais</th>
                            <th>🔍 Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dayTrackings.map((tracking) => (
                            <tr key={tracking.id}>
                              <td>{formatTime(tracking.clockInTime)}</td>
                              <td>{formatTime(tracking.clockOutTime)}</td>
                              <td>
                                <strong>{formatDuration(tracking.totalMinutes)}</strong>
                              </td>
                              <td>
                                <Badge bg="success">
                                  {formatDuration(tracking.totalStationaryMinutes || 0)}
                                </Badge>
                              </td>
                              <td>
                                <Badge bg="warning">
                                  {formatDuration(tracking.totalTravelMinutes || 0)}
                                </Badge>
                              </td>
                              <td>
                                <Badge bg="info">
                                  {tracking.locationClusters?.length || 0} locais
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => showLocationDetails(tracking)}
                                  className="me-2"
                                >
                                  Detalhes
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDelete(tracking.id)}
                                >
                                  🗑️ Deletar
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>

                {/* Resumo do dia */}
                <Row className="mt-4 g-3">
                  <Col md={3}>
                    <Card className="stat-card">
                      <Card.Body className="text-center">
                        <div className="stat-value">{dayTrackings.length}</div>
                        <div className="stat-label">Sessões</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="stat-card">
                      <Card.Body className="text-center">
                        <div className="stat-value">
                          {formatDuration(dayTrackings.reduce((sum, t) => sum + (t.totalMinutes || 0), 0))}
                        </div>
                        <div className="stat-label">Total de Horas</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="stat-card">
                      <Card.Body className="text-center">
                        <div className="stat-value">
                          {formatDuration(dayTrackings.reduce((sum, t) => sum + (t.totalStationaryMinutes || 0), 0))}
                        </div>
                        <div className="stat-label">🏠 Tempo Parado</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="stat-card">
                      <Card.Body className="text-center">
                        <div className="stat-value">
                          {formatDuration(dayTrackings.reduce((sum, t) => sum + (t.totalTravelMinutes || 0), 0))}
                        </div>
                        <div className="stat-label">✈️ Trajeto</div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {/* Resumo geral */}
      {trackings.length > 0 && (
        <Card className="mt-4">
          <Card.Header className="bg-transparent border-0 pb-0">
            <h5 className="mb-0">📈 Resumo Geral</h5>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body className="text-center">
                    <div className="stat-value">{trackings.length}</div>
                    <div className="stat-label">Total Sessões</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body className="text-center">
                    <div className="stat-value">
                      {formatDuration(trackings.reduce((sum, t) => sum + (t.totalMinutes || 0), 0))}
                    </div>
                    <div className="stat-label">Horas Totais</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body className="text-center">
                    <div className="stat-value">
                      {formatDuration(trackings.reduce((sum, t) => sum + (t.totalStationaryMinutes || 0), 0))}
                    </div>
                    <div className="stat-label">Parado Total</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="stat-card">
                  <Card.Body className="text-center">
                    <div className="stat-value">
                      {formatDuration(trackings.reduce((sum, t) => sum + (t.totalTravelMinutes || 0), 0))}
                    </div>
                    <div className="stat-label">Trajeto Total</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}
